import { useQuery } from '@tanstack/react-query';

import { fetchBranding } from '@/api/branding';
import { queryKeys } from '@/api/queryKeys';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

/**
 * How a tenant is identified on screen: their **logo** if they have uploaded one, their **name** if
 * they have not (§2, §6.10; TASK-102).
 *
 * ⚠️ **An `<img>`, not `background-image: var(--logo)`.** `TenantBrand` does set `--logo` — it is one
 * of the three properties §2 names, and a decorative CSS consumer can use it — but a background image
 * has **no accessible name**: a screen reader hears nothing where a sighted reader sees whose workspace
 * this is. The mark is the tenant's identity on a page that may be the only thing telling somebody they
 * are signing in to the right place (§9, and `access.test.tsx`'s reasoning about a page wearing a
 * tenant's branding), so it gets an element that can carry `alt` text.
 *
 * ⚠️ **The `alt` is the tenant's name, not "logo".** Somebody who cannot see the mark needs the same
 * fact a sighted reader gets from it — *whose* workspace — and "Logo" is a word about the image rather
 * than about the workspace.
 *
 * ⚠️ **An unbranded tenant is indistinguishable from before this release**: the name in `type.eyebrow`,
 * exactly as the sidebar and the sign-in screen have rendered it since Sprint 8. That is asserted, so a
 * tenant that brands nothing is not quietly given an empty box where a mark would go.
 *
 * It reads the **same** query entry the shell and the sign-in screen already read for the name, so this
 * is one source read in more places rather than a second source: the properties are applied once
 * (`TenantBrand`), and this renders.
 */
export function TenantMark({ fallback, className }: { fallback?: string; className?: string }) {
  const branding = useQuery({
    queryKey: queryKeys.branding(),
    queryFn: fetchBranding,
    staleTime: Infinity,
  });

  const name = branding.data?.tenant?.name ?? fallback ?? '';
  const logoUrl = branding.data?.tenant?.logo_url ?? null;

  if (logoUrl !== null) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className={cn('h-8 w-auto max-w-[168px] object-contain object-left', className)}
      />
    );
  }

  return <p className={cn(type.eyebrow, className)}>{name}</p>;
}
